import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import {
  createEvent,
  getEvents,
  deleteEvent,
  updateEvent,
} from "../../api/eventsApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import DeleteConfirmationModal from "../../components/deleteModal";
import { useNavigate } from "react-router-dom";
import EventFormModal from "./EventFormModal";
import { useAuthStore } from "../../store/auth.store"; // ← CHANGE 1: import store

const EventPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const isViewOnly = useAuthStore((s) => s.isViewOnly()); // ← CHANGE 2: read flag

  const LIMIT = 10;

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteEventId, setDeleteEventId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const statusStyle = (status) => {
    switch (status) {
      case "published":
        return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "draft":
        return "text-amber-400 bg-amber-500/10 border-amber-500/20";
      case "cancelled":
        return "text-red-400 bg-red-500/10 border-red-500/20";
      default:
        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "published":
        return "Published";
      case "cancelled":
        return "Cancelled";
      case "draft":
        return "Draft";
      case "completed":
        return "Completed";
      default:
        return status;
    }
  };

  const handleEventSubmit = async (info) => {
    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", info.name);
      formData.append("date_start", info.date_start);
      formData.append("date_end", info.date_end);
      // formData.append("sold_tickets", info.sold_tickets);
      // formData.append("invitations_sent", info.invitations_sent);
      // formData.append("refundable_tickets", info.refundable_tickets);
      // formData.append("amount", info.amount);
      //. formData.append("currency", info.currency);

      // Only append poster if it's a File
      // if (info.poster instanceof File) {
      //   formData.append("poster", info.poster);
      // }

      formData.append("map_address", info.map_address);
      formData.append("map_latitude", info.map_latitude);
      formData.append("map_longitude", info.map_longitude);

      if (info.flyer_portrait instanceof File) {
        formData.append("flyer_portrait", info.flyer_portrait);
      }

      if (info.flyer_horizontal instanceof File) {
        formData.append("flyer_horizontal", info.flyer_horizontal);
      }

      if (editingEvent) {
        await updateEvent(editingEvent.id, formData);
      } else {
        await createEvent(formData);
      }

      setEditingEvent(null);
      setShowAddModal(false);

      const offset = (page - 1) * LIMIT;
      const data = await getEvents(LIMIT, offset);
      setEvents(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEventDelete = async () => {
    try {
      setDeleting(true);
      await deleteEvent(deleteEventId);

      setDeleteEventId(null);

      // Refresh events list
      const offset = (page - 1) * LIMIT;
      const data = await getEvents(LIMIT, offset);
      setEvents(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      alert("Failed to delete the event");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError("");

        const offset = (page - 1) * LIMIT;
        const data = await getEvents(LIMIT, offset);
        console.log('------', data);

        setEvents(data.results || []);
        setTotal(data.count || 0);
      } catch (err) {
        setError("Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Events Info</h1>
        </div>

        {/* CHANGE 3a: hide New Event button for viewers */}
        {!isViewOnly && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            <PlusIcon className="w-4 h-4" />
            New Event
          </button>
        )}
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4 text-center">Poster</th>
                <th className="px-6 py-4 text-center">Name</th>
                <th className="px-6 py-4 text-center">Event start date</th>
                <th className="px-6 py-4 text-center">Event end date</th>
                <th className="px-6 py-4 text-center">Refundable tickets</th>
                <th className="px-6 py-4 text-center">Sold tickets</th>
                <th className="px-6 py-4 text-center">Invitations send</th>
                <th className="px-6 py-4 text-center">To be settled</th>
                <th className="px-6 py-4 text-center">Status</th>
                {/* CHANGE 3b: hide Actions column header for viewers */}
                {!isViewOnly && (
                  <th className="px-6 py-4 text-center">Actions</th>
                )}
              </tr>
            </thead>

            <tbody>
              {events.map((event) => (
                <tr
                  key={event.id}
                  onClick={() => navigate(`/dashboard/events/${event.id}`)}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition cursor-pointer"
                >
                  <td className="px-6 py-4">
                    {event.flyer_portrait ? (
                      <img
                        className="w-20 h-20 object-contain rounded-sm"
                        src={event.flyer_portrait}
                        alt="-"
                      />
                    ) : (
                      <div className="text-sm text-center font-medium text-slate-300">
                        -
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-center font-medium text-slate-300">
                      {event.name}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.date_start.split("T")[0]}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.date_end.split("T")[0]}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.refundable_tickets || "-"}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.sold_tickets}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.invitations_sent}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.currency} {event.amount}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${statusStyle(event.status)}`}
                    >
                      {statusLabel(event.status)}
                    </span>
                  </td>

                  {/* CHANGE 3c: hide edit/delete buttons for viewers */}
                  {!isViewOnly && (
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEvent(event);
                          }}
                          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteEventId(event.id);
                          }}
                          className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                          title="Delete event"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {events.length === 0 && (
          <div className="p-8 text-center text-slate-400">No events found.</div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals only mount for non-viewers — no way to trigger them anyway */}
      {!isViewOnly && (
        <>
          <EventFormModal
            isOpen={showAddModal || !!editingEvent}
            onClose={() => {
              setShowAddModal(false);
              setEditingEvent(null);
            }}
            onSubmit={handleEventSubmit}
            loading={saving}
            mode={editingEvent ? "edit" : "create"}
            initialData={editingEvent}
          />

          <DeleteConfirmationModal
            isOpen={!!deleteEventId}
            description="Are you sure you want to delete this event? This action cannot be undone."
            onClose={() => setDeleteEventId(null)}
            onConfirm={handleEventDelete}
            loading={deleting}
          />
        </>
      )}

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default EventPage;
